import React, { useState, useEffect } from 'react';
import { adminAPI, settingsAPI } from '../../services/api';
import Navbar from '../../components/Navbar';

function SurgeAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedArea, setSelectedArea] = useState(null);
  const [manualMultiplier, setManualMultiplier] = useState(1.0);
  const [actionLoading, setActionLoading] = useState(false);

  // Surge pricing settings state
  const [surgeSettings, setSurgeSettings] = useState(null);
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [editMaxMultiplier, setEditMaxMultiplier] = useState(2.5);
  const [editPeakHours, setEditPeakHours] = useState([]);
  const [settingsSaving, setSettingsSaving] = useState(false);

  useEffect(() => {
    fetchSurgeAnalytics();
    fetchSurgeSettings();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchSurgeAnalytics, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSurgeSettings = async () => {
    try {
      const response = await settingsAPI.getSettings();
      const sp = response.data.data?.surgePricing || response.data.surgePricing || {};
      setSurgeSettings(sp);
      setEditMaxMultiplier(sp.maxMultiplier || 2.5);
      setEditPeakHours(sp.thresholds?.peakHours || []);
    } catch (error) {
      console.error('Failed to fetch surge settings:', error);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    try {
      await adminAPI.updateSurgePricingSettings({
        maxMultiplier: editMaxMultiplier,
        peakHours: editPeakHours,
      });
      await fetchSurgeSettings();
      alert('Surge settings saved successfully!');
    } catch (error) {
      alert('Failed to save: ' + (error.response?.data?.message || error.message));
    } finally {
      setSettingsSaving(false);
    }
  };

  const addPeakHour = () => {
    setEditPeakHours([...editPeakHours, { startHour: 12, endHour: 14, multiplier: 1.2 }]);
  };

  const updatePeakHour = (index, field, value) => {
    const updated = [...editPeakHours];
    updated[index] = { ...updated[index], [field]: Number(value) };
    setEditPeakHours(updated);
  };

  const removePeakHour = (index) => {
    setEditPeakHours(editPeakHours.filter((_, i) => i !== index));
  };

  const fetchSurgeAnalytics = async () => {
    try {
      const response = await adminAPI.getSurgeAnalytics();
      setData(response.data.data);
    } catch (error) {
      console.error('Failed to fetch surge analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRecalculate = async (workAreaId) => {
    setActionLoading(true);
    try {
      await adminAPI.recalculateSurge(workAreaId);
      await fetchSurgeAnalytics();
      alert('Surge recalculated successfully! Auto-calculation re-enabled.');
    } catch (error) {
      alert('Failed to recalculate surge: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSetMultiplier = async (workAreaId) => {
    setActionLoading(true);
    try {
      await adminAPI.setSurgeMultiplier(workAreaId, manualMultiplier);
      await fetchSurgeAnalytics();
      setSelectedArea(null);
      alert('Surge multiplier updated! Auto-calculation disabled for this area.');
    } catch (error) {
      alert('Failed to set multiplier: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleAutoCalculation = async (workAreaId, currentEnabled) => {
    setActionLoading(true);
    try {
      await adminAPI.toggleSurgeAutoCalculation(workAreaId, !currentEnabled);
      await fetchSurgeAnalytics();
    } catch (error) {
      alert('Failed to toggle auto-calculation: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const getSurgeColor = (multiplier) => {
    if (multiplier >= 1.4) return '#dc2626'; // Red
    if (multiplier >= 1.2) return '#f97316'; // Orange
    if (multiplier > 1.0) return '#eab308'; // Yellow
    return '#22c55e'; // Green
  };

  const formatTime = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleTimeString('en-IN', {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) return <div className="loading">Loading surge analytics...</div>;

  return (
    <div style={{ background: 'linear-gradient(135deg, #f5f7fa 0%, #e8eef3 100%)', minHeight: '100vh' }}>
      <Navbar />
      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }} className="fade-in">
          <h1 style={{
            fontSize: 'var(--font-size-3xl)',
            fontWeight: '800',
            background: 'linear-gradient(135deg, #f97316 0%, #dc2626 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: 'var(--space-sm)'
          }}>
            Surge Pricing Analytics
          </h1>
          <p style={{ color: 'var(--gray-600)', fontSize: 'var(--font-size-md)' }}>
            Real-time surge pricing monitoring and control
          </p>
        </div>

        {/* Surge Settings Panel */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <div
            onClick={() => setSettingsExpanded(!settingsExpanded)}
            style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              cursor: 'pointer', padding: '16px 20px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '20px' }}>&#9881;</span>
              <h3 style={{ margin: 0, fontSize: 'var(--font-size-lg)', fontWeight: '700' }}>
                Surge Configuration
              </h3>
            </div>
            <span style={{ fontSize: '18px', color: 'var(--gray-500)' }}>
              {settingsExpanded ? '▲' : '▼'}
            </span>
          </div>
          {settingsExpanded && surgeSettings && (
            <div style={{ padding: '0 20px 20px', borderTop: '1px solid var(--gray-200)' }}>
              {/* Max Multiplier Cap */}
              <div style={{ marginTop: '16px', marginBottom: '20px' }}>
                <label style={{ fontWeight: '600', fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', display: 'block', marginBottom: '6px' }}>
                  Max Surge Multiplier Cap
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input
                    type="range" min="1.0" max="5.0" step="0.1"
                    value={editMaxMultiplier}
                    onChange={(e) => setEditMaxMultiplier(parseFloat(e.target.value))}
                    style={{ flex: 1 }}
                  />
                  <span style={{
                    fontWeight: '700', fontSize: 'var(--font-size-xl)',
                    color: editMaxMultiplier >= 3 ? '#dc2626' : editMaxMultiplier >= 2 ? '#f97316' : '#22c55e',
                    minWidth: '50px', textAlign: 'center'
                  }}>
                    {editMaxMultiplier.toFixed(1)}x
                  </span>
                </div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginTop: '4px' }}>
                  Auto-calculated surge will never exceed this value. Current: {surgeSettings.maxMultiplier || 2.5}x
                </p>
              </div>

              {/* Engine Info */}
              <div style={{
                background: 'var(--gray-50)', borderRadius: '8px', padding: '12px 16px',
                marginBottom: '20px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px',
              }}>
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>Calculation Interval</div>
                  <div style={{ fontWeight: '600' }}>Every 2 min</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>Demand Window</div>
                  <div style={{ fontWeight: '600' }}>20 minutes</div>
                </div>
                <div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>Cooldown</div>
                  <div style={{ fontWeight: '600' }}>Max -0.1/cycle</div>
                </div>
              </div>

              {/* Peak Hours */}
              <div style={{ marginBottom: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <label style={{ fontWeight: '600', fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)' }}>
                    Peak Hour Presets
                  </label>
                  <button
                    onClick={addPeakHour}
                    style={{
                      padding: '4px 12px', background: '#3B82F6', color: 'white',
                      border: 'none', borderRadius: '6px', fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                    }}
                  >
                    + Add Window
                  </button>
                </div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginBottom: '10px' }}>
                  Set minimum surge during known peak hours. Auto-calculated surge uses max(demand, peak preset).
                </p>
                {editPeakHours.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '16px', color: 'var(--gray-400)', fontSize: 'var(--font-size-sm)' }}>
                    No peak hour presets configured. Surge is purely demand-driven.
                  </div>
                )}
                {editPeakHours.map((ph, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 12px', background: 'var(--gray-50)', borderRadius: '8px',
                    marginBottom: '8px'
                  }}>
                    <select
                      value={ph.startHour}
                      onChange={(e) => updatePeakHour(i, 'startHour', e.target.value)}
                      style={{ padding: '6px', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                    <span style={{ color: 'var(--gray-500)' }}>to</span>
                    <select
                      value={ph.endHour}
                      onChange={(e) => updatePeakHour(i, 'endHour', e.target.value)}
                      style={{ padding: '6px', borderRadius: '4px', border: '1px solid var(--gray-300)' }}
                    >
                      {Array.from({ length: 24 }, (_, h) => (
                        <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
                      ))}
                    </select>
                    <span style={{ color: 'var(--gray-500)' }}>@</span>
                    <input
                      type="number" min="1.0" max="3.0" step="0.1"
                      value={ph.multiplier}
                      onChange={(e) => updatePeakHour(i, 'multiplier', e.target.value)}
                      style={{ width: '65px', padding: '6px', borderRadius: '4px', border: '1px solid var(--gray-300)', textAlign: 'center' }}
                    />
                    <span style={{ fontWeight: '600', color: getSurgeColor(ph.multiplier) }}>x</span>
                    <button
                      onClick={() => removePeakHour(i)}
                      style={{
                        background: 'none', border: 'none', color: '#dc2626',
                        cursor: 'pointer', fontSize: '16px', padding: '4px'
                      }}
                    >
                      &#10005;
                    </button>
                  </div>
                ))}
              </div>

              {/* Weather Info */}
              <div style={{
                background: '#EFF6FF', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px'
              }}>
                <div style={{ fontWeight: '600', fontSize: 'var(--font-size-sm)', color: '#1D4ED8', marginBottom: '4px' }}>
                  Weather-Based Surge
                </div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: '#3B82F6', margin: 0 }}>
                  Automatically adjusts surge based on weather conditions. Thunderstorm: 1.2x, Rain: 1.1-1.15x,
                  Extreme temperature ({'>'}42°C / {'<'}5°C): 1.1x. Weather data cached for 15 min per city.
                  Requires OPENWEATHERMAP_API_KEY environment variable.
                </p>
              </div>

              {/* Prediction Info */}
              <div style={{
                background: '#F0FDF4', borderRadius: '8px', padding: '12px 16px', marginBottom: '20px'
              }}>
                <div style={{ fontWeight: '600', fontSize: 'var(--font-size-sm)', color: '#16A34A', marginBottom: '4px' }}>
                  Surge Prediction
                </div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: '#22C55E', margin: 0 }}>
                  Users see "Surge may reduce in ~X min" in their cart when the trend is declining.
                  Prediction uses the last 5 surge history entries and the 0.1/cycle cooldown rate.
                </p>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSaveSettings}
                disabled={settingsSaving}
                className="btn"
                style={{
                  width: '100%', padding: '12px', background: '#f97316', color: 'white',
                  border: 'none', borderRadius: '8px', fontSize: 'var(--font-size-md)',
                  fontWeight: '700', cursor: settingsSaving ? 'not-allowed' : 'pointer'
                }}
              >
                {settingsSaving ? 'Saving...' : 'Save Surge Configuration'}
              </button>
            </div>
          )}
        </div>

        {/* Summary Cards */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)' }}>
          <h3 style={{ fontSize: 'var(--font-size-xl)', fontWeight: '700', marginBottom: 'var(--space-lg)' }}>
            Overview
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 'var(--space-md)'
          }}>
            <div className="stat-card" style={{ borderLeftColor: '#f97316' }}>
              <div className="stat-icon">🗺️</div>
              <div className="stat-value">{data?.summary?.totalWorkAreas || 0}</div>
              <div className="stat-label">Work Areas</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#dc2626' }}>
              <div className="stat-icon">🔥</div>
              <div className="stat-value">{data?.summary?.areasWithSurge || 0}</div>
              <div className="stat-label">Areas with Surge</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#8b5cf6' }}>
              <div className="stat-icon">🤖</div>
              <div className="stat-value">{data?.summary?.areasInAutoMode || 0}</div>
              <div className="stat-label">Auto Mode</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#ec4899' }}>
              <div className="stat-icon">✋</div>
              <div className="stat-value">{data?.summary?.areasInManualMode || 0}</div>
              <div className="stat-label">Manual Mode</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#eab308' }}>
              <div className="stat-icon">📈</div>
              <div className="stat-value">{data?.summary?.highestSurge?.toFixed(1) || '1.0'}x</div>
              <div className="stat-label">Highest Surge</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#22c55e' }}>
              <div className="stat-icon">📦</div>
              <div className="stat-value">{data?.summary?.totalActiveOrders || 0}</div>
              <div className="stat-label">Active Orders</div>
            </div>
            <div className="stat-card" style={{ borderLeftColor: '#3b82f6' }}>
              <div className="stat-icon">🚴</div>
              <div className="stat-value">{data?.summary?.totalAvailablePartners || 0}</div>
              <div className="stat-label">Available Partners</div>
            </div>
          </div>
        </div>

        {/* Mode Explanation */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)', background: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)' }}>
          <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', marginBottom: 'var(--space-md)', color: '#3730a3' }}>
            ℹ️ Auto vs Manual Mode
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-lg)' }}>
            <div style={{ background: 'rgba(255,255,255,0.7)', padding: 'var(--space-md)', borderRadius: '8px' }}>
              <p style={{ fontWeight: '700', color: '#3730a3', marginBottom: '4px' }}>
                🤖 Auto Mode (Default)
              </p>
              <p style={{ color: '#4338ca', fontSize: 'var(--font-size-sm)' }}>
                Surge is calculated automatically every 5 minutes based on demand/supply ratio.
              </p>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.7)', padding: 'var(--space-md)', borderRadius: '8px' }}>
              <p style={{ fontWeight: '700', color: '#be185d', marginBottom: '4px' }}>
                ✋ Manual Mode
              </p>
              <p style={{ color: '#9d174d', fontSize: 'var(--font-size-sm)' }}>
                Surge is set manually by admin and won't be overwritten until you re-enable auto mode.
              </p>
            </div>
          </div>
        </div>

        {/* Surge Formula Explanation */}
        <div className="card fade-in" style={{ marginBottom: 'var(--space-xl)', background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)' }}>
          <h3 style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', marginBottom: 'var(--space-md)', color: '#92400e' }}>
            📊 Surge Calculation Formula
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-lg)' }}>
            <div>
              <p style={{ fontWeight: '600', color: '#78350f', marginBottom: 'var(--space-sm)' }}>
                {data?.formula?.description}
              </p>
              <code style={{
                display: 'block',
                background: 'rgba(255,255,255,0.7)',
                padding: 'var(--space-sm)',
                borderRadius: '8px',
                fontFamily: 'monospace',
                fontSize: 'var(--font-size-md)',
                color: '#dc2626'
              }}>
                {data?.formula?.formula}
              </code>
              <p style={{ marginTop: 'var(--space-sm)', color: '#92400e' }}>
                <strong>Cap:</strong> {data?.formula?.cap} | <strong>Interval:</strong> {data?.formula?.calculationInterval}
              </p>
            </div>
            <div>
              <p style={{ fontWeight: '600', color: '#78350f', marginBottom: 'var(--space-sm)' }}>Example Scenarios:</p>
              <table style={{ width: '100%', fontSize: 'var(--font-size-sm)' }}>
                <thead>
                  <tr style={{ color: '#78350f' }}>
                    <th style={{ textAlign: 'left', padding: '4px' }}>Demand Ratio</th>
                    <th style={{ textAlign: 'left', padding: '4px' }}>Surge</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.formula?.examples?.map((ex, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? 'rgba(255,255,255,0.5)' : 'transparent' }}>
                      <td style={{ padding: '4px' }}>{ex.demandRatio}</td>
                      <td style={{ padding: '4px', fontWeight: '600' }}>{ex.surge}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Work Areas Grid */}
        <div className="card fade-in">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-lg)' }}>
            <h3 style={{ fontSize: 'var(--font-size-xl)', fontWeight: '700' }}>
              Work Area Surge Status
            </h3>
            <button
              onClick={fetchSurgeAnalytics}
              className="btn"
              style={{ background: 'var(--primary)', color: 'white' }}
            >
              🔄 Refresh
            </button>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
            gap: 'var(--space-lg)'
          }}>
            {data?.workAreas?.map((area) => (
              <div
                key={area._id}
                style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: 'var(--space-lg)',
                  border: `3px solid ${getSurgeColor(area.currentSurgeMultiplier)}`,
                  boxShadow: area.hasSurge ? `0 4px 20px ${getSurgeColor(area.currentSurgeMultiplier)}40` : 'var(--shadow-sm)'
                }}
              >
                {/* Area Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-md)' }}>
                  <div>
                    <h4 style={{ fontSize: 'var(--font-size-lg)', fontWeight: '700', marginBottom: '4px' }}>
                      {area.name}
                    </h4>
                    <p style={{ color: 'var(--gray-500)', fontSize: 'var(--font-size-sm)' }}>
                      {area.city}
                    </p>
                  </div>
                  <div style={{
                    background: getSurgeColor(area.currentSurgeMultiplier),
                    color: 'white',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    fontWeight: '700',
                    fontSize: 'var(--font-size-xl)'
                  }}>
                    {area.currentSurgeMultiplier.toFixed(1)}x
                  </div>
                </div>

                {/* Auto/Manual Mode Badge */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 'var(--space-md)',
                  padding: '8px 12px',
                  background: area.autoCalculation?.enabled ? '#e0f2fe' : '#fce7f3',
                  borderRadius: '8px'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '20px' }}>
                      {area.autoCalculation?.enabled ? '🤖' : '✋'}
                    </span>
                    <span style={{
                      fontWeight: '600',
                      color: area.autoCalculation?.enabled ? '#0369a1' : '#be185d'
                    }}>
                      {area.autoCalculation?.enabled ? 'Auto Mode' : 'Manual Mode'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleToggleAutoCalculation(area._id, area.autoCalculation?.enabled)}
                    disabled={actionLoading}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '6px',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '600',
                      fontSize: 'var(--font-size-sm)',
                      background: area.autoCalculation?.enabled ? '#fce7f3' : '#e0f2fe',
                      color: area.autoCalculation?.enabled ? '#be185d' : '#0369a1'
                    }}
                  >
                    {area.autoCalculation?.enabled ? 'Switch to Manual' : 'Enable Auto'}
                  </button>
                </div>

                {/* Current Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 'var(--space-sm)',
                  marginBottom: 'var(--space-sm)',
                  padding: 'var(--space-sm)',
                  background: 'var(--gray-50)',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: '700', color: '#22c55e' }}>
                      {area.currentStats.activeOrders || 0}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>Active Orders</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: '700', color: '#3b82f6' }}>
                      {area.currentStats.availablePartners}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>Partners</div>
                  </div>
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 'var(--space-sm)',
                  marginBottom: 'var(--space-md)',
                  padding: 'var(--space-sm)',
                  background: 'var(--gray-50)',
                  borderRadius: '8px'
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: '700', color: '#f97316' }}>
                      {area.currentStats.ordersInLastHour}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>Orders (1hr)*</div>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 'var(--font-size-xl)', fontWeight: '700', color: '#8b5cf6' }}>
                      {area.currentStats.demandRatio}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>Demand Ratio</div>
                  </div>
                </div>
                <p style={{ fontSize: '10px', color: 'var(--gray-400)', marginBottom: 'var(--space-sm)' }}>
                  *Orders (1hr) used for surge calculation
                </p>

                {/* Last Calculated */}
                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)', marginBottom: 'var(--space-md)' }}>
                  Last calculated: {formatTime(area.lastSurgeCalculatedAt)}
                </p>

                {/* Surge History Mini Chart */}
                {area.surgeHistory && area.surgeHistory.length > 0 && (
                  <div style={{ marginBottom: 'var(--space-md)' }}>
                    <p style={{ fontSize: 'var(--font-size-sm)', fontWeight: '600', marginBottom: '8px' }}>
                      Surge History (Last {area.surgeHistory.length} calculations)
                    </p>
                    <div style={{
                      display: 'flex',
                      alignItems: 'flex-end',
                      height: '40px',
                      gap: '2px',
                      background: 'var(--gray-50)',
                      padding: '4px',
                      borderRadius: '6px'
                    }}>
                      {area.surgeHistory.map((h, idx) => (
                        <div
                          key={idx}
                          title={`${h.multiplier.toFixed(2)}x at ${formatTime(h.calculatedAt)}`}
                          style={{
                            flex: 1,
                            height: `${((h.multiplier - 1) / 0.5) * 100}%`,
                            minHeight: '4px',
                            background: getSurgeColor(h.multiplier),
                            borderRadius: '2px'
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                  <button
                    onClick={() => handleRecalculate(area._id)}
                    disabled={actionLoading}
                    className="btn"
                    style={{
                      flex: 1,
                      background: 'var(--gray-100)',
                      color: 'var(--gray-700)',
                      padding: '8px',
                      fontSize: 'var(--font-size-sm)'
                    }}
                    title="Recalculate surge and enable auto mode"
                  >
                    🔄 Recalculate
                  </button>
                  <button
                    onClick={() => {
                      setSelectedArea(area);
                      setManualMultiplier(area.currentSurgeMultiplier);
                    }}
                    className="btn"
                    style={{
                      flex: 1,
                      background: 'var(--primary)',
                      color: 'white',
                      padding: '8px',
                      fontSize: 'var(--font-size-sm)'
                    }}
                    title="Set manual surge (disables auto calculation)"
                  >
                    ✏️ Set Manual
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Manual Multiplier Modal */}
        {selectedArea && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              padding: 'var(--space-xl)',
              width: '400px',
              maxWidth: '90vw'
            }}>
              <h3 style={{ marginBottom: 'var(--space-lg)' }}>
                Set Surge for {selectedArea.name}
              </h3>
              <p style={{ marginBottom: 'var(--space-md)', color: 'var(--gray-600)' }}>
                Current: {selectedArea.currentSurgeMultiplier.toFixed(1)}x
              </p>

              {/* Warning about auto-calculation */}
              <div style={{
                background: '#fef3c7',
                border: '1px solid #f59e0b',
                borderRadius: '8px',
                padding: 'var(--space-md)',
                marginBottom: 'var(--space-lg)'
              }}>
                <p style={{ color: '#92400e', fontSize: 'var(--font-size-sm)', margin: 0 }}>
                  ⚠️ <strong>Note:</strong> Setting a manual surge will disable automatic calculation for this area.
                  The surge will stay at your set value until you click "Recalculate" or "Enable Auto".
                </p>
              </div>

              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                  New Multiplier (1.0 - 1.5)
                </label>
                <input
                  type="range"
                  min="1.0"
                  max="1.5"
                  step="0.1"
                  value={manualMultiplier}
                  onChange={(e) => setManualMultiplier(parseFloat(e.target.value))}
                  style={{ width: '100%', marginBottom: '8px' }}
                />
                <div style={{
                  textAlign: 'center',
                  fontSize: 'var(--font-size-2xl)',
                  fontWeight: '700',
                  color: getSurgeColor(manualMultiplier)
                }}>
                  {manualMultiplier.toFixed(1)}x
                </div>
              </div>
              <div style={{ display: 'flex', gap: 'var(--space-sm)' }}>
                <button
                  onClick={() => setSelectedArea(null)}
                  className="btn"
                  style={{ flex: 1, background: 'var(--gray-200)', color: 'var(--gray-700)' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSetMultiplier(selectedArea._id)}
                  disabled={actionLoading}
                  className="btn"
                  style={{ flex: 1, background: getSurgeColor(manualMultiplier), color: 'white' }}
                >
                  {actionLoading ? 'Saving...' : 'Save & Disable Auto'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SurgeAnalytics;
